import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiSearch, FiChevronDown, FiChevronUp } = FiIcons;

const Pricing = () => {
  const [activeCategory, setActiveCategory] = useState('instagram');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [expandedService, setExpandedService] = useState(null);

  // Categories for the service tabs
  const categories = [
    { id: 'instagram', name: 'Instagram', icon: 'instagram' },
    { id: 'youtube', name: 'YouTube', icon: 'youtube' },
    { id: 'tiktok', name: 'TikTok', icon: 'music' },
    { id: 'twitter', name: 'Twitter', icon: 'twitter' },
    { id: 'facebook', name: 'Facebook', icon: 'facebook' },
    { id: 'telegram', name: 'Telegram', icon: 'send' }
  ];

  // Services data organized by category
  const services = {
    instagram: [
      { id: 1, name: 'Instagram Followers', description: 'High-quality followers with profile pictures', minOrder: 100, maxOrder: 10000, price: 1.99, delivery: 'Instant Start (500/day)', category: 'instagram' },
      { id: 2, name: 'Instagram Likes', description: 'Real-looking likes from active accounts', minOrder: 50, maxOrder: 5000, price: 0.99, delivery: 'Instant Start (1000/day)', category: 'instagram' },
      { id: 3, name: 'Instagram Views', description: 'Fast video views to boost engagement', minOrder: 500, maxOrder: 50000, price: 0.49, delivery: 'Instant Start (5000/day)', category: 'instagram' },
      { id: 4, name: 'Instagram Comments', description: 'Custom comments from real-looking profiles', minOrder: 10, maxOrder: 1000, price: 3.99, delivery: '1-2 days (100/day)', category: 'instagram' },
      { id: 5, name: 'Instagram Story Views', description: 'Quick story views to increase visibility', minOrder: 100, maxOrder: 10000, price: 0.79, delivery: 'Instant Start (2000/day)', category: 'instagram' }
    ],
    youtube: [
      { id: 6, name: 'YouTube Views', description: 'High retention video views', minOrder: 1000, maxOrder: 100000, price: 1.49, delivery: '1-2 days (10000/day)', category: 'youtube' },
      { id: 7, name: 'YouTube Subscribers', description: 'Real-looking subscribers with profile pictures', minOrder: 100, maxOrder: 5000, price: 2.99, delivery: 'Gradual Delivery (300/day)', category: 'youtube' },
      { id: 8, name: 'YouTube Likes', description: 'Video likes to improve engagement ratio', minOrder: 50, maxOrder: 10000, price: 1.29, delivery: 'Instant Start (1000/day)', category: 'youtube' },
      { id: 9, name: 'YouTube Comments', description: 'Custom comments for better engagement', minOrder: 10, maxOrder: 500, price: 4.99, delivery: '1-2 days (50/day)', category: 'youtube' }
    ],
    tiktok: [
      { id: 10, name: 'TikTok Followers', description: 'Active TikTok followers', minOrder: 100, maxOrder: 10000, price: 2.49, delivery: 'Gradual Delivery (500/day)', category: 'tiktok' },
      { id: 11, name: 'TikTok Likes', description: 'Fast likes for TikTok videos', minOrder: 100, maxOrder: 20000, price: 0.99, delivery: 'Instant Start (2000/day)', category: 'tiktok' },
      { id: 12, name: 'TikTok Views', description: 'High-quality video views', minOrder: 1000, maxOrder: 100000, price: 0.29, delivery: 'Instant Start (10000/day)', category: 'tiktok' }
    ],
    twitter: [
      { id: 13, name: 'Twitter Followers', description: 'Real-looking Twitter followers', minOrder: 100, maxOrder: 5000, price: 2.99, delivery: 'Gradual Delivery (300/day)', category: 'twitter' },
      { id: 14, name: 'Twitter Likes', description: 'Tweet likes from active accounts', minOrder: 50, maxOrder: 10000, price: 1.49, delivery: 'Instant Start (1000/day)', category: 'twitter' },
      { id: 15, name: 'Twitter Retweets', description: 'Retweets to increase reach', minOrder: 50, maxOrder: 5000, price: 1.99, delivery: 'Instant Start (500/day)', category: 'twitter' }
    ],
    facebook: [
      { id: 16, name: 'Facebook Page Likes', description: 'High-quality page likes', minOrder: 100, maxOrder: 10000, price: 2.99, delivery: 'Gradual Delivery (300/day)', category: 'facebook' },
      { id: 17, name: 'Facebook Post Likes', description: 'Likes for your posts', minOrder: 50, maxOrder: 5000, price: 1.49, delivery: 'Instant Start (1000/day)', category: 'facebook' },
      { id: 18, name: 'Facebook Video Views', description: 'Views for your videos', minOrder: 1000, maxOrder: 50000, price: 0.99, delivery: 'Instant Start (5000/day)', category: 'facebook' }
    ],
    telegram: [
      { id: 19, name: 'Telegram Channel Members', description: 'Real members for your channel', minOrder: 100, maxOrder: 5000, price: 2.49, delivery: 'Gradual Delivery (300/day)', category: 'telegram' },
      { id: 20, name: 'Telegram Post Views', description: 'Views for your channel posts', minOrder: 100, maxOrder: 50000, price: 0.49, delivery: 'Instant Start (5000/day)', category: 'telegram' }
    ]
  };

  // Filter services based on search and active category
  const filteredServices = activeCategory 
    ? services[activeCategory].filter(service => 
        service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : Object.values(services).flat().filter(service => 
        service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
      );

  // Sort services by price
  const sortedServices = [...filteredServices].sort((a, b) => {
    return sortOrder === 'asc' ? a.price - b.price : b.price - a.price;
  });

  const toggleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const toggleServiceExpand = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <section id="pricing" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our Services & Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse our comprehensive list of services. We offer competitive pricing with high-quality delivery and 24/7 support.
          </p>
        </motion.div>

        {/* Search and Filter Bar */}
        <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-4"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-md text-sm font-medium ${!activeCategory ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              All Services
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${activeCategory === category.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Services Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Min/Max Order
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={toggleSort}>
                    <div className="flex items-center">
                      Price
                      <SafeIcon 
                        icon={sortOrder === 'asc' ? FiChevronUp : FiChevronDown} 
                        className="ml-1 w-4 h-4" 
                      />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedServices.map((service) => (
                  <React.Fragment key={service.id}>
                    <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => toggleServiceExpand(service.id)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {service.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-semibold">{service.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {service.minOrder} - {service.maxOrder}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        ${service.price.toFixed(2)} per 1000
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button className="text-blue-600 hover:text-blue-800">
                          <SafeIcon 
                            icon={expandedService === service.id ? FiChevronUp : FiChevronDown} 
                            className="w-5 h-5" 
                          />
                        </button>
                      </td>
                    </tr>
                    {expandedService === service.id && (
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">Description</h4>
                              <p className="text-sm text-gray-900">{service.description}</p>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">Delivery Speed</h4>
                              <p className="text-sm text-gray-900">{service.delivery}</p>
                            </div>
                            <div>
                              <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-md font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200">
                                Order Now
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {sortedServices.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No services found matching your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white shadow-lg">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <h3 className="text-2xl font-bold mb-2">Need Custom Services?</h3>
              <p className="text-blue-100">Contact our support team for custom orders and bulk pricing.</p>
            </div>
            <a 
              href="mailto:info@premiumsmmpanel.io"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-md"
            >
              Contact Support
            </a>
          </div>
        </div>

        {/* Volume Discount */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Volume Discounts</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$50 - $99</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">5% off</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$100 - $499</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">10% off</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$500 - $999</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">15% off</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$1000+</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">20% off</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;